import { OrcamentoIcon as DashboardIcon, OrcamentoFeedbackProvider } from '../components/OrcamentoUI';
import { useMaquetesTheme } from '../context/MaquetesThemeContext';
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';
import './DashboardTheme.css';

import CompanySettingsModal from '../components/Dashboard/CompanySettingsModal';
import PainelValidades from './PainelValidades';

const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return dt.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// Ícone por origem do alerta: documentos da empresa (certidões e seguros),
// dos trabalhadores (identificação, aptidão médica, visto) e dos veículos.
const TIPO_ICON = {
    empresa: '🏛️',
    pessoal: '👷',
    veiculo: '🚚',
};

const alertColor = (dias) => {
    if (dias < 0) return { bg: '#fff0f0', border: '#fca5a5', text: '#b91c1c', badge: '#fee2e2', badgeText: '#991b1b' };
    if (dias <= 7) return { bg: '#fff7ed', border: '#fdba74', text: '#c2410c', badge: '#ffedd5', badgeText: '#9a3412' };
    if (dias <= 30) return { bg: '#fffbeb', border: '#fcd34d', text: '#b45309', badge: '#fef3c7', badgeText: '#92400e' };
    return { bg: '#f0fdf4', border: '#86efac', text: '#166534', badge: '#dcfce7', badgeText: '#15803d' };
};

const DashboardContent = () => {
    const { theme } = useMaquetesTheme();
    const liquid = theme === 'liquido';
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [stats, setStats] = useState({ obras: 0, trabalhadores: 0, equipamentos: 0, orcamentos: 0, veiculos: 0 });
    const [recentWorks, setRecentWorks] = useState([]);
    const [recentOrcamentos, setRecentOrcamentos] = useState([]);
    const [alertas, setAlertas] = useState([]);
    /* As validades de tudo o que tem data na empresa. Estava no Sistema
       Integrado, que é do sistema de gestão; viaturas, contratos e certidões não
       são matéria dele. Fica fechado até alguém o abrir — o painel faz o seu
       próprio pedido e o Dashboard já mostra em cima o que expira a 60 dias. */
    const [verValidades, setVerValidades] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // As Notificações mandam para aqui quem clica num documento da empresa: as
    // certidões e os seguros só se editam nesta janela.
    const [showCompanyModal, setShowCompanyModal] = useState(!!location.state?.abrirDadosEmpresa);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [fastRes, alertasRes] = await Promise.all([
                api.get('/dashboard/fast').catch(() => ({ data: null })),
                api.get('/dashboard/alertas').catch(() => ({ data: [] }))
            ]);

            const fast = fastRes.data;
            if (fast?.stats) {
                setStats({
                    obras: fast.stats.obras || 0,
                    trabalhadores: fast.stats.trabalhadores || 0,
                    equipamentos: fast.stats.equipamentos || 0,
                    orcamentos: fast.stats.orcamentos || 0,
                    veiculos: fast.stats.veiculos || 0,
                });
                setRecentWorks(fast.recentObras || []);
                setRecentOrcamentos(fast.recentOrcamentos || []);
            }
            setAlertas(alertasRes.data || []);
        } catch (err) {
            console.error('Erro ao carregar dashboard:', err);
            setError('Erro ao carregar dados. Verifique a ligação ao servidor.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadAll(); }, [loadAll]);

    const statCards = [
        { title: 'Obras', value: stats.obras, icon: '🏗️', color: '#4299e1', path: '/obras', subtitle: 'Total de obras registadas' },
        { title: 'Trabalhadores', value: stats.trabalhadores, icon: '👷', color: '#48bb78', path: '/trabalhadores', subtitle: 'Ficheiros de pessoal' },
        { title: 'Equipamentos', value: stats.equipamentos, icon: '🚜', color: '#ed8936', path: '/equipamentos', subtitle: 'Máquinas e ferramentas' },
        { title: 'Veículos', value: stats.veiculos, icon: '🚚', color: '#9f7aea', path: '/veiculos', subtitle: 'Frota de veículos' },
        { title: 'Orçamentos', value: stats.orcamentos, icon: '📋', color: '#38b2ac', path: '/orcamentos', subtitle: 'Total de orçamentos' }
    ];

    const alertasExpirados = alertas.filter(a => a.dias < 0).length;
    const alertasUrgentes = alertas.filter(a => a.dias >= 0 && a.dias <= 7).length;

    return (
        <div className="dashboard">
            {/* Top Bar */}
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><DashboardIcon name="dashboard" fallback="" /> Dashboard</h1>
                        <p>Visão geral do sistema</p>
                    </div>
                </div>
                <div className="top-bar-actions">
                    {user?.role === 'admin' && (
                        <button className="btn-secondary" onClick={() => setShowCompanyModal(true)}>
                            <DashboardIcon name="settings" fallback="⚙️" /> Dados Empresa
                        </button>
                    )}
                    <button className="btn-secondary" onClick={loadAll} disabled={loading}>
                        {loading ? 'A carregar...' : <><DashboardIcon name="refresh" fallback="🔄" /> Atualizar</>}
                    </button>
                </div>
            </div>

            <div className="page-content">
                {error && (
                    <div style={{ backgroundColor: '#fff5f5', color: '#c53030', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #feb2b2' }}>
                        <DashboardIcon name="warning" fallback="⚠️" /> {error}
                    </div>
                )}

                {/* Stats */}
                <div className="stats-grid">
                    {statCards.map((card, index) => (
                        <div key={index} className="stat-card" role="button" tabIndex={0} aria-label={`${card.title}: ${card.value}`} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(card.path); } }} onClick={() => navigate(card.path)} style={{ cursor: 'pointer', '--stat-accent': card.color }}>
                            <div className="stat-icon" style={{ background: `${card.color}20` }}>
                                <DashboardIcon name={{ '/obras': 'building', '/trabalhadores': 'users', '/equipamentos': 'equipment', '/veiculos': 'vehicle', '/orcamentos': 'document' }[card.path]} fallback={card.icon} />
                            </div>
                            <div className="stat-content">
                                <h3 className="stat-value">{loading ? '...' : card.value}</h3>
                                <p className="stat-title">{card.title}</p>
                                <span className="stat-subtitle">{card.subtitle}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Obras + Orçamentos */}
                <div className="dashboard-sections">
                    <div className="section-card">
                        <h2><DashboardIcon name="building" fallback="🏗️" /> Obras Recentes</h2>
                        <div className="dashboard-table-container">
                            {recentWorks.length > 0 ? (
                                <table className="dashboard-table standard-table">
                                    <thead>
                                        <tr>
                                            <th>Obra / Local</th>
                                            <th>Cliente</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentWorks.map(obra => (
                                            <tr key={obra.id} onClick={() => navigate(`/obras/${obra.id}`)} style={{ cursor: 'pointer' }}>
                                                <td style={{ fontWeight: '500' }}>{obra.posicao || obra.nome}</td>
                                                <td>{obra.cliente}</td>
                                                <td>
                                                    <span className={`status-badge ${obra.status}`}>
                                                        {liquid ? ({ planeada: 'Planeada', em_curso: 'Em curso', concluida: 'Concluída', suspensa: 'Suspensa', cancelada: 'Cancelada' }[obra.status] || obra.status || 'Planeada') : obra.status || 'planeada'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="empty-state">
                                    <p>Nenhuma obra registada ainda</p>
                                    <button className="btn-primary" onClick={() => navigate('/obras/nova')}>+ Nova Obra</button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="section-card">
                        <h2><DashboardIcon name="document" fallback="📋" /> Orçamentos Recentes</h2>
                        <div className="dashboard-table-container">
                            {recentOrcamentos.length > 0 ? (
                                <table className="dashboard-table standard-table">
                                    <thead>
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Projeto</th>
                                            <th>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentOrcamentos.map(orc => (
                                            <tr key={orc.id} onClick={() => navigate(`/orcamentos/${orc.id}`)} style={{ cursor: 'pointer' }}>
                                                <td style={{ fontWeight: '500' }}>{orc.cliente || '-'}</td>
                                                <td>{orc.projeto || '-'}</td>
                                                <td>
                                                    <span className={`status-badge ${orc.estado}`}>
                                                        {liquid ? ({ EM_ORCAMENTO: 'Em orçamento', EM_PRODUCAO: 'Em produção', PARA_FATURAR: 'Para faturar', ENVIADO: 'Enviado', CONCLUIDO: 'Concluído', FATURADO: 'Faturado', FACTURADO: 'Faturado', CANCELADO: 'Cancelado', PENDENTE: 'Pendente' }[orc.estado] || orc.estado?.replace(/_/g, ' ') || 'Em orçamento') : orc.estado || 'EM_ORCAMENTO'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="empty-state">
                                    <p>Nenhum orçamento registado ainda</p>
                                    <button className="btn-primary" onClick={() => navigate('/orcamentos')}>+ Novo Orçamento</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom: Ações Rápidas + Alertas */}
                <div className="dashboard-bottom">
                    {/* Ações Rápidas */}
                    <div className="quick-actions section-card">
                        <h2>Ações Rápidas</h2>
                        <div className="actions-grid">
                            <button className="quick-action-card qa-encomenda" onClick={() => navigate('/orcamentos', { state: { tab: 'encomendas', openNova: true } })}>
                                <span><DashboardIcon name="equipment" fallback="📦" /></span>
                                <span>Nova Encomenda</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/obras/nova')}>
                                <span><DashboardIcon name="building" fallback="🏗️" /></span>
                                <span>Nova Obra</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/trabalhadores', { state: { openModal: true } })}>
                                <span><DashboardIcon name="users" fallback="👷" /></span>
                                <span>Novo Trabalhador</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/equipamentos')}>
                                <span><DashboardIcon name="equipment" fallback="🚜" /></span>
                                <span>Equipamentos</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/veiculos')}>
                                <span><DashboardIcon name="vehicle" fallback="🚚" /></span>
                                <span>Veículos</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/despesas')}>
                                <span><DashboardIcon name="document" fallback="📝" /></span>
                                <span>Despesas</span>
                            </button>
                            <button className="quick-action-card" onClick={() => navigate('/relatorios')}>
                                <span><DashboardIcon name="chart" fallback="📊" /></span>
                                <span>Relatórios</span>
                            </button>
                        </div>
                    </div>

                    {/* Alertas e Avisos */}
                    <div className="alertas-panel section-card">
                        <h2>
                            <DashboardIcon name="warning" fallback="⚠️" /> Alertas e Avisos
                            {alertas.length > 0 && (
                                <span className="alertas-count-badge">
                                    {alertas.length}
                                    {(alertasExpirados + alertasUrgentes) > 0 && (
                                        <span className="alertas-urgente-dot" title="Expirados ou urgentes" />
                                    )}
                                </span>
                            )}
                        </h2>
                        <div className="alertas-list">
                            {loading ? (
                                <div className="alertas-empty">A carregar...</div>
                            ) : alertas.length === 0 ? (
                                <div className="alertas-empty">
                                    <span style={{ fontSize: 28 }}><DashboardIcon name="check" fallback="✅" /></span>
                                    <p>Sem documentos a expirar nos próximos 60 dias</p>
                                </div>
                            ) : (
                                alertas.map((a, i) => {
                                    const isOnboarding = a.tipo === 'onboarding';
                                    const c = isOnboarding
                                        ? { bg: '#f0fff4', border: '#86efac', badge: '#16a34a', badgeText: '#fff' }
                                        : alertColor(a.dias);
                                    const tipoIcon = isOnboarding ? '✍️' : (TIPO_ICON[a.tipo] || '📄');
                                    const diasAtras = Math.abs(a.dias);
                                    const diasLabel = isOnboarding
                                        ? (diasAtras === 0 ? 'Hoje' : `Há ${diasAtras} dia${diasAtras !== 1 ? 's' : ''}`)
                                        : (a.dias < 0
                                            ? `Expirado há ${diasAtras} dia${diasAtras !== 1 ? 's' : ''}`
                                            : a.dias === 0 ? 'Expira hoje'
                                            : `${a.dias} dia${a.dias !== 1 ? 's' : ''}`);
                                    return (
                                        <div key={i} className="alerta-item" data-tone={isOnboarding ? 'success' : a.dias < 0 ? 'danger' : a.dias <= 7 ? 'urgent' : a.dias <= 30 ? 'warning' : 'info'} style={{ background: c.bg, borderColor: c.border }}>
                                            <span className="alerta-tipo-icon"><DashboardIcon name={{ empresa: 'building', pessoal: 'users', veiculo: 'vehicle', onboarding: 'edit' }[a.tipo] || 'document'} fallback={tipoIcon} /></span>
                                            <div className="alerta-info">
                                                <span className="alerta-nome" title={a.nome}>{a.nome}</span>
                                                <span className="alerta-doc">{a.documento}</span>
                                            </div>
                                            <div className="alerta-right">
                                                <span className="alerta-validade">{fmtDate(a.validade)}</span>
                                                <span className="alerta-dias-badge" style={{ background: c.badge, color: c.badgeText }}>
                                                    {diasLabel}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-card dashboard-validities" style={{ marginTop: 18 }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <DashboardIcon name="clock" fallback="⏰" /> Validades da empresa e da frota
                    <button className="btn-secondary" style={{ marginLeft: 'auto', padding: '6px 14px', fontSize: '0.85rem' }}
                        aria-expanded={verValidades} aria-controls="dashboard-validities-panel" onClick={() => setVerValidades(v => !v)}>
                        {verValidades ? 'Ocultar' : 'Ver todas'}
                    </button>
                </h2>
                <p style={{ color: '#718096', fontSize: '0.88rem', marginTop: 0 }}>
                    Certidões e seguros da empresa, documentos e contratos dos trabalhadores, inspeções
                    e seguros das viaturas, revisões dos equipamentos e verificações das instalações.
                    Os prazos do sistema de gestão estão no Sistema Integrado, em Validades de cada área.
                </p>
                {verValidades && <div id="dashboard-validities-panel" className="dashboard-validities-panel"><PainelValidades liquid={liquid} /></div>}
            </div>

            {showCompanyModal && (
                <CompanySettingsModal onClose={() => setShowCompanyModal(false)} />
            )}
        </div>
    );
};

export default function Dashboard() {
    return <OrcamentoFeedbackProvider><DashboardContent /></OrcamentoFeedbackProvider>;
}
